import {
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Badge } from "../../components/ui/misc";
import { layoutBoxLabels } from "../../lib/box-labels";
import { boxColorFor } from "../../lib/cabinet-colors";
import { renderPageToCanvas } from "../../lib/pdf";

export type BoxRect = { box_x: number; box_y: number; box_w: number; box_h: number };

export type CanvasItem = BoxRect & {
  id: string;
  label: string;
  cabinet_type_code: string;
  size_status: string;
  /** 框体旋转角度（度）：户型图斜着拍的时候用它摆正 */
  rotation_deg?: number;
  /** 刚画出来、还在后台创建：先画在图上，等正式条目回来再换成真 id。 */
  pending?: boolean;
};

/** 标尺：图上的一段线段 + 它代表的真实长度（mm） */
export type CanvasRuler = { x1: number; y1: number; x2: number; y2: number; lengthMm: number };

/** 画布视口：scale 缩放、x/y 平移（像素）。 */
export type CanvasView = { scale: number; x: number; y: number };

export const MIN_SCALE = 0.25;
export const MAX_SCALE = 5;

export type PdfCanvasProps = {
  url: string;
  /** 图片型图纸：直接显示图片，不走 pdf.js。 */
  imageUrl?: string | null;
  pageIndex: number;
  /** 受控视口：工具栏的放大缩小和这里的滚轮/平移共用同一份状态。 */
  view: CanvasView;
  onViewChange: (view: CanvasView) => void;
  /** 画框模式：空白处拖拽 = 新建框；否则空白处拖拽 = 平移画布。 */
  drawMode: boolean;
  /** 当前页已保存的标尺 */
  ruler?: CanvasRuler | null;
  /** 标尺是否被选中（选中后右侧面板可以改实际长度 / 删除） */
  rulerSelected?: boolean;
  onRulerSelect?: (selected: boolean) => void;
  /** 双击标尺（打开右侧属性面板 / 聚焦长度输入） */
  onRulerEdit?: () => void;
  /** 拖动/旋转/拉伸标尺后的提交（实际长度不变，改的是线段位置与长度） */
  onRulerChange?: (segment: { x1: number; y1: number; x2: number; y2: number }) => void;
  /** 旋转框体（度） */
  onRotate?: (itemId: string, rotationDeg: number) => void;
  items: CanvasItem[];
  selectedId: string | null;
  onSelect: (itemId: string | null) => void;
  onCreate: (rect: BoxRect) => void;
  /** 画完一框后回调：父组件据此把"一次性画框工具"切回选择模式。 */
  onDrawn?: () => void;
  onUpdate: (itemId: string, rect: BoxRect) => void;
};

/**
 * 鼠标状态机（全部放在 gestureRef 里，不进 React state，保证跟手不掉帧）：
 *
 *   idle    什么都没按
 *   pan     平移画布：中键拖动 / 空格+左键 / 选择模式下按在空白处拖动
 *   create  画框：画框模式下（或按了 R）在空白处拖动
 *   move    拖框：按在框体内部
 *   resize  拉伸框：按在框体右下角手柄
 *   rotate  旋转框：按在框体上方的旋转手柄
 *   ruler   量标尺：标尺模式下在空白处拖一条线
 *   ruler-move      拖动整条标尺
 *   ruler-endpoint  拖标尺两端（同时改变长度与角度）
 *
 * 判定优先级（pointerdown 时一次定好，中途不再切换）：
 *   中键 → pan
 *   空格按住 → pan
 *   命中 [data-rotate-handle] → rotate
 *   命中 [data-resize-handle] → resize
 *   命中 [data-item-id] → move（创建中的临时框不参与拖动）
 *   标尺模式 → ruler
 *   画框模式 → create
 *   其余空白 → pan（选择模式下的默认行为，和 Canva/Figma 一致）
 */
type Gesture =
  | {
      kind: "create";
      startX: number;
      startY: number;
      currentX: number;
      currentY: number;
      /** 按下时的屏幕坐标：判断"这次到底算不算画"用像素，避免放大后画小框被丢掉 */
      startClientX: number;
      startClientY: number;
    }
  | { kind: "move" | "resize"; itemId: string; startClientX: number; startClientY: number; origin: BoxRect }
  | {
      kind: "rotate";
      itemId: string;
      centerClientX: number;
      centerClientY: number;
      startAngle: number;
      startRotation: number;
    }
  | {
      kind: "ruler-move";
      startClientX: number;
      startClientY: number;
      origin: { x1: number; y1: number; x2: number; y2: number };
    }
  | { kind: "ruler-endpoint"; handle: "start" | "end" }
  | {
      kind: "pan";
      startClientX: number;
      startClientY: number;
      originX: number;
      originY: number;
      moved: boolean;
    };

/**
 * 画框的最小归一化尺寸：只用来兜底，防止画出 0 宽 / 0 高的退化框。
 * 以前这里是 0.02 并且**小于它就丢掉整次绘制**——放大画布后画小柜子时，
 * 框占整页的比例天然很小，一松手就"消失"了。现在只在屏幕像素上判断"算不算一次绘制"。
 */
const MIN_BOX_SIZE = 0.003;
/** 指针位移超过这么多像素才算"画框"，否则当成一次点击（避免误触凭空多一个柜体）。 */
const DRAW_SLOP = 3;
/** 标尺端标的半长（px）：一段垂直于标尺的细竖条（细一点才看得出终点在哪） */
const RULER_TICK_HALF = 9;
/** 标尺默认红色，和柜体的蓝色框区分开 */
const RULER_COLOR = "#dc2626";
const RULER_COLOR_ACTIVE = "#b91c1c";
/** 平移时超过这个像素才算"拖动"，否则当成一次点击（空白点击 = 取消选中）。 */
const CLICK_SLOP = 4;

/**
 * 编号标签的大小**跟着框体走**：标签高 = 框体短边 × 这个比例。
 * 柜子小（卫生间、吊柜这些）编号就小，不会把图纸糊住；柜子大编号就大，远看也认得出。
 * 比例与最小高度这两个数是"手感值"，觉得偏大/偏小改这两个即可。
 */
const LABEL_BOX_RATIO = 0.12;
/** 再小的框也得留个能看清的下限（页面坐标 px） */
const LABEL_MIN_HEIGHT = 9;
const LABEL_FONT_STACK = "ui-sans-serif, system-ui, -apple-system, sans-serif";
let labelMeasureContext: CanvasRenderingContext2D | null = null;
function measureLabelText(text: string, fontSize: number): number {
  if (!labelMeasureContext) labelMeasureContext = window.document.createElement("canvas").getContext("2d");
  if (!labelMeasureContext) return text.length * 6;
  labelMeasureContext.font = `500 ${fontSize}px ${LABEL_FONT_STACK}`;
  return labelMeasureContext.measureText(text).width;
}

/** 一个框的编号标签尺寸：高度、字号、内边距（都按框体短边算） */
type LabelMetrics = { height: number; fontSize: number; padding: number };
function labelMetricsFor(boxW: number, boxH: number): LabelMetrics {
  const height = Math.max(LABEL_MIN_HEIGHT, Math.min(boxW, boxH) * LABEL_BOX_RATIO);
  return { height, fontSize: height * 0.62, padding: Math.max(2, height * 0.22) };
}

export function PdfCanvas({
  url,
  imageUrl,
  pageIndex,
  view,
  onViewChange,
  drawMode,
  ruler,
  rulerSelected,
  onRulerSelect,
  onRulerEdit,
  onRulerChange,
  onRotate,
  items,
  selectedId,
  onSelect,
  onCreate,
  onDrawn,
  onUpdate,
}: PdfCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  /** 真正被 translate/scale 的那一层（外框保持不动，只动内容）。 */
  const stageRef = useRef<HTMLDivElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  /** 标尺对象（可拖动/旋转/拉伸）：线、两端手柄、标签都直接改 DOM，松手才提交 */
  const rulerGroupRef = useRef<SVGGElement | null>(null);
  const rulerRef = useRef(ruler);
  rulerRef.current = ruler;
  const boxRefs = useRef(new Map<string, HTMLDivElement>());
  const gestureRef = useRef<Gesture | null>(null);
  const pointerRef = useRef({ clientX: 0, clientY: 0 });
  const frameRef = useRef<number | null>(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  /** 键盘快捷键用的最新值（effect 只注册一次，直接闭包会拿到过期的选中 id） */
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const onRotateRef = useRef(onRotate);
  onRotateRef.current = onRotate;
  const rulerSelectedRef = useRef(rulerSelected);
  rulerSelectedRef.current = rulerSelected;
  const onRulerChangeRef = useRef(onRulerChange);
  onRulerChangeRef.current = onRulerChange;
  /** 读最新值的 ref：手势里要用的 props 一律从这里取，避免闭包过期。 */
  const drawModeRef = useRef(drawMode);
  drawModeRef.current = drawMode;
  const viewRef = useRef(view);
  viewRef.current = view;
  const spaceRef = useRef(false);

  const [pageSize, setPageSize] = useState<{ width: number; height: number } | null>(null);
  const [pageLabel, setPageLabel] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (imageUrl) return; // 图片型图纸直接显示图片
    let cancelled = false;
    const render = async () => {
      try {
        const canvas = canvasRef.current;
        const overlay = overlayRef.current;
        if (!canvas || !overlay) return;
        const available = viewportRef.current?.clientWidth || 900;
        // 按容器宽度渲染一次；之后的缩放/平移交给 CSS transform（不重新解码）
        const rendered = await renderPageToCanvas({
          url,
          pageIndex,
          canvas,
          cssWidth: available,
          devicePixelRatio: window.devicePixelRatio || 1,
        });
        if (!cancelled) {
          setPageSize({ width: rendered.cssWidth, height: rendered.cssHeight });
          setPageLabel(`${pageIndex + 1} / ${rendered.pageCount}`);
          setError(null);
        }
      } catch (caught) {
        if (!cancelled) setError(caught instanceof Error ? caught.message : "PDF 渲染失败");
      }
    };
    void render();
    let timer: number | undefined;
    const onResize = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => void render(), 150);
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      window.removeEventListener("resize", onResize);
    };
  }, [url, pageIndex, imageUrl]);

  /** 图片型图纸：用图片的自然尺寸当页面尺寸。 */
  useEffect(() => {
    if (!imageUrl) return;
    const image = new Image();
    image.onload = () => {
      setPageSize({ width: image.naturalWidth, height: image.naturalHeight });
      setPageLabel("图片");
    };
    image.src = imageUrl;
  }, [imageUrl]);

  useEffect(
    () => () => {
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  /** 按住空格 = 临时进入平移模式（和设计工具一致）。 */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      // [ / ] 旋转选中框（Shift 精细 1°）
      if ((event.key === "[" || event.key === "]") && selectedIdRef.current && onRotateRef.current) {
        const currentId = selectedIdRef.current;
        const item = itemsRef.current.find((entry) => entry.id === currentId);
        if (item) {
          const step = event.shiftKey ? 1 : 5;
          const delta = event.key === "[" ? -step : step;
          onRotateRef.current(currentId, Math.round(normalizeDeg((item.rotation_deg ?? 0) + delta)));
          event.preventDefault();
        }
        return;
      }
      // 标尺选中时，[] 旋转标尺（绕中点）
      if ((event.key === "[" || event.key === "]") && rulerSelectedRef.current && rulerRef.current) {
        const segment = rulerRef.current;
        const step = event.shiftKey ? 1 : 5;
        const delta = ((event.key === "[" ? -step : step) * Math.PI) / 180;
        const cx = (segment.x1 + segment.x2) / 2;
        const cy = (segment.y1 + segment.y2) / 2;
        const rotatePoint = (x: number, y: number) => ({
          x: cx + (x - cx) * Math.cos(delta) - (y - cy) * Math.sin(delta),
          y: cy + (x - cx) * Math.sin(delta) + (y - cy) * Math.cos(delta),
        });
        const a = rotatePoint(segment.x1, segment.y1);
        const b = rotatePoint(segment.x2, segment.y2);
        onRulerChangeRef.current?.({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
        event.preventDefault();
        return;
      }
      if (event.code !== "Space") return;
      spaceRef.current = true;
      if (viewportRef.current) viewportRef.current.style.cursor = "grab";
      event.preventDefault();
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code !== "Space") return;
      spaceRef.current = false;
      const overlay = overlayRef.current;
      if (overlay) overlay.style.cursor = drawModeRef.current ? "crosshair" : "default";
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  const normalized = useCallback((clientX: number, clientY: number) => {
    const overlay = overlayRef.current;
    if (!overlay) return { x: 0, y: 0 };
    const rect = overlay.getBoundingClientRect();
    return {
      x: clamp((clientX - rect.left) / rect.width, 0, 1),
      y: clamp((clientY - rect.top) / rect.height, 0, 1),
    };
  }, []);

  const rectForPointer = useCallback((gesture: Gesture, clientX: number, clientY: number): BoxRect | null => {
    // 只有"拖框/拉伸框"需要算矩形，其余手势（画框/平移/标尺/旋转）不走这里
    if (gesture.kind !== "move" && gesture.kind !== "resize") return null;
    const overlay = overlayRef.current;
    if (!overlay) return null;
    // 注意：这里是"缩放后"的屏幕尺寸，框的坐标是归一化的，所以缩放/平移都不影响换算
    const bounds = overlay.getBoundingClientRect();
    const screenDx = (clientX - gesture.startClientX) / bounds.width;
    const screenDy = (clientY - gesture.startClientY) / bounds.height;
    // 框旋转后，把屏幕位移投影回框自己的坐标系，拉伸才不会斜着跑
    const item = itemsRef.current.find((entry) => entry.id === gesture.itemId);
    const rotation = ((item?.rotation_deg ?? 0) * Math.PI) / 180;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const dx = screenDx * cos + screenDy * sin;
    const dy = -screenDx * sin + screenDy * cos;
    if (gesture.kind === "move") {
      return {
        box_x: clamp(gesture.origin.box_x + dx, 0, 1 - gesture.origin.box_w),
        box_y: clamp(gesture.origin.box_y + dy, 0, 1 - gesture.origin.box_h),
        box_w: gesture.origin.box_w,
        box_h: gesture.origin.box_h,
      };
    }
    return {
      box_x: gesture.origin.box_x,
      box_y: gesture.origin.box_y,
      box_w: clamp(gesture.origin.box_w + dx, MIN_BOX_SIZE, 1 - gesture.origin.box_x),
      box_h: clamp(gesture.origin.box_h + dy, MIN_BOX_SIZE, 1 - gesture.origin.box_y),
    };
  }, []);

  /** 直接改 DOM，不经过 React：这是拖动跟手的关键。 */
  const paint = useCallback(() => {
    frameRef.current = null;
    const gesture = gestureRef.current;
    if (!gesture) return;
    const { clientX, clientY } = pointerRef.current;

    if (gesture.kind === "pan") {
      const dx = clientX - gesture.startClientX;
      const dy = clientY - gesture.startClientY;
      if (Math.abs(dx) > CLICK_SLOP || Math.abs(dy) > CLICK_SLOP) gesture.moved = true;
      const stage = stageRef.current;
      if (!stage) return;
      // 平移同样直接改 DOM，松手时再提交给 React
      stage.style.transform = `translate(${gesture.originX + dx}px, ${gesture.originY + dy}px) scale(${viewRef.current.scale})`;
      return;
    }

    if (gesture.kind === "ruler-move" || gesture.kind === "ruler-endpoint") {
      const overlay = overlayRef.current;
      const size = overlayRect(overlay);
      if (!overlay || !size.width) return;
      const bounds = overlay.getBoundingClientRect();
      const segment = rulerRef.current;
      if (!segment) return;
      if (gesture.kind === "ruler-move") {
        const dx = (clientX - gesture.startClientX) / bounds.width;
        const dy = (clientY - gesture.startClientY) / bounds.height;
        paintRulerObject(rulerGroupRef.current, size, fitSegment(gesture.origin, dx, dy));
        return;
      }
      // 拖端点：端点跟手指走（既能拉长又能转）
      const point = normalized(clientX, clientY);
      const next =
        gesture.handle === "start"
          ? { x1: point.x, y1: point.y, x2: segment.x2, y2: segment.y2 }
          : { x1: segment.x1, y1: segment.y1, x2: point.x, y2: point.y };
      paintRulerObject(rulerGroupRef.current, size, next);
      return;
    }

    if (gesture.kind === "create") {
      const preview = previewRef.current;
      if (!preview) return;
      preview.style.display = "block";
      setElementRect(preview, {
        box_x: Math.min(gesture.startX, gesture.currentX),
        box_y: Math.min(gesture.startY, gesture.currentY),
        box_w: Math.abs(gesture.currentX - gesture.startX),
        box_h: Math.abs(gesture.currentY - gesture.startY),
      });
      return;
    }

    if (gesture.kind === "rotate") {
      const element = boxRefs.current.get(gesture.itemId);
      if (!element) return;
      const angle = angleFromCenter(gesture.centerClientX, gesture.centerClientY, clientX, clientY);
      const next = normalizeDeg(gesture.startRotation + (angle - gesture.startAngle));
      element.style.transform = `rotate(${next}deg)`;
      return;
    }

    // 只有"拖框/拉伸框"才走到这里
    if (gesture.kind !== "move" && gesture.kind !== "resize") return;
    const element = boxRefs.current.get(gesture.itemId);
    const overlay = overlayRef.current;
    if (!element || !overlay) return;
    const bounds = overlay.getBoundingClientRect();
    if (gesture.kind === "move") {
      if (gesture.kind !== "move" && gesture.kind !== "resize") return;
      const rect = rectForPointer(gesture, clientX, clientY);
      if (!rect) return;
      // 移动只改 transform：走合成层、不触发布局，框不会抖
      element.style.transform = `translate(${(rect.box_x - gesture.origin.box_x) * bounds.width}px, ${
        (rect.box_y - gesture.origin.box_y) * bounds.height
      }px)`;
      return;
    }
    const rect = rectForPointer(gesture, clientX, clientY);
    if (!rect) return;
    setElementRect(element, rect);
  }, [rectForPointer]);

  const schedulePaint = useCallback(() => {
    if (frameRef.current) return;
    frameRef.current = window.requestAnimationFrame(paint);
  }, [paint]);

  const finish = useCallback(
    (clientX: number, clientY: number) => {
      const gesture = gestureRef.current;
      gestureRef.current = null;
      const overlay = overlayRef.current;
      if (overlay) overlay.style.cursor = drawModeRef.current ? "crosshair" : "default";
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      if (!gesture) return;

      if (gesture.kind === "pan") {
        const dx = clientX - gesture.startClientX;
        const dy = clientY - gesture.startClientY;
        // 没动过 = 一次空白点击：取消选中；动过才提交平移
        if (!gesture.moved) {
          onSelect(null);
          return;
        }
        onViewChange({ ...viewRef.current, x: gesture.originX + dx, y: gesture.originY + dy });
        return;
      }

      if (gesture.kind === "create") {
        if (previewRef.current) previewRef.current.style.display = "none";
        // "有没有画"看的是屏幕位移（像素），不是占整页的比例：放大后画小框也一样能画出来
        const movedPx = Math.hypot(clientX - gesture.startClientX, clientY - gesture.startClientY);
        if (movedPx < DRAW_SLOP) return;
        const point = normalized(clientX, clientY);
        // 兜底到最小尺寸（仍然留在页面内），保证框可见、抓得住
        const boxW = Math.min(Math.max(Math.abs(point.x - gesture.startX), MIN_BOX_SIZE), 1);
        const boxH = Math.min(Math.max(Math.abs(point.y - gesture.startY), MIN_BOX_SIZE), 1);
        const rect = {
          box_x: clamp(Math.min(gesture.startX, point.x), 0, 1 - boxW),
          box_y: clamp(Math.min(gesture.startY, point.y), 0, 1 - boxH),
          box_w: boxW,
          box_h: boxH,
        };
        onCreate(rect);
        onDrawn?.(); // 一次性工具：画完就交还给父组件决定是否退出画框
        return;
      }

      if (gesture.kind === "ruler-move" || gesture.kind === "ruler-endpoint") {
        const overlay = overlayRef.current;
        const bounds = overlay?.getBoundingClientRect();
        if (!overlay || !bounds) return;
        const point = normalized(clientX, clientY);
        let next: { x1: number; y1: number; x2: number; y2: number };
        if (gesture.kind === "ruler-move") {
          next = fitSegment(
            gesture.origin,
            (clientX - gesture.startClientX) / bounds.width,
            (clientY - gesture.startClientY) / bounds.height,
          );
        } else {
          const current = rulerRef.current;
          if (!current) return;
          // 拖端点：只移动那一个端点，另一头保持不动（同时改变长度与角度）
          next =
            gesture.handle === "start"
              ? { x1: point.x, y1: point.y, x2: current.x2, y2: current.y2 }
              : { x1: current.x1, y1: current.y1, x2: point.x, y2: point.y };
        }
        onRulerChange?.(next);
        return;
      }

      if (gesture.kind === "rotate") {
        const element = boxRefs.current.get(gesture.itemId);
        if (element) element.style.zIndex = "";
        const angle = angleFromCenter(gesture.centerClientX, gesture.centerClientY, clientX, clientY);
        const next = normalizeDeg(gesture.startRotation + (angle - gesture.startAngle));
        onRotate?.(gesture.itemId, Math.round(next));
        return;
      }

      const rect = rectForPointer(gesture, clientX, clientY);
      if (!rect) return;
      // 松手时把最终坐标直接写回 DOM（撤掉拖动用的 transform），
      // 这样"拖动预览 → 正式渲染"是同一帧同一位置，不会闪回。
      const element = boxRefs.current.get(gesture.itemId);
      if (element) {
        element.style.transform = "";
        setElementRect(element, rect);
      }
      onUpdate(gesture.itemId, rect);
    },
    [normalized, onCreate, onDrawn, onRotate, onUpdate, onViewChange, rectForPointer],
  );

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const target = event.target as HTMLElement;
    const boxElement = target.closest<HTMLElement>("[data-item-id]");
    const itemId = boxElement?.dataset.itemId;
    // 中键 / 右键 / 空格 → 一定平移（画框模式下也能平移，不用先切工具）
    const wantsPan = event.button === 1 || event.button === 2 || spaceRef.current;

    // 1) 中键 / 空格 → 一定是平移
    if (wantsPan) {
      event.preventDefault();
      gestureRef.current = {
        kind: "pan",
        startClientX: event.clientX,
        startClientY: event.clientY,
        originX: viewRef.current.x,
        originY: viewRef.current.y,
        moved: false,
      };
      pointerRef.current = { clientX: event.clientX, clientY: event.clientY };
      overlay.style.cursor = "grabbing";
      capturePointer(overlay, event.pointerId);
      return;
    }

    if (event.button !== 0) return;

    // 1.5) 标尺是个可操作对象：点线身拖动、点两端手柄拉伸/旋转
    const rulerElement = target.closest<HTMLElement>("[data-ruler]");
    const currentRuler = rulerRef.current;
    if (rulerElement && currentRuler) {
      onSelect(null);
      onRulerSelect?.(true);
      const handle = target.closest<HTMLElement>("[data-ruler-handle]")?.dataset.rulerHandle;
      if (handle === "start" || handle === "end") {
        gestureRef.current = { kind: "ruler-endpoint", handle };
      } else {
        gestureRef.current = {
          kind: "ruler-move",
          startClientX: event.clientX,
          startClientY: event.clientY,
          origin: { ...currentRuler },
        };
      }
      pointerRef.current = { clientX: event.clientX, clientY: event.clientY };
      overlay.style.cursor = "grabbing";
      capturePointer(overlay, event.pointerId);
      return;
    }

    // 2) 空白处
    if (!itemId) {
      onRulerSelect?.(false);
      if (drawMode) {
        onSelect(null);
        const point = normalized(event.clientX, event.clientY);
        gestureRef.current = {
          kind: "create",
          startX: point.x,
          startY: point.y,
          currentX: point.x,
          currentY: point.y,
          startClientX: event.clientX,
          startClientY: event.clientY,
        };
        pointerRef.current = { clientX: event.clientX, clientY: event.clientY };
        capturePointer(overlay, event.pointerId);
        schedulePaint();
        return;
      }
      // 选择模式下空白拖拽 = 平移画布
      // 注意：这里【不能】调 onSelect(null)——那会触发 React 重渲染，
      // 把中途直接写进 DOM 的平移预览覆盖回旧值，表现就是"拖不动"。
      gestureRef.current = {
        kind: "pan",
        startClientX: event.clientX,
        startClientY: event.clientY,
        originX: viewRef.current.x,
        originY: viewRef.current.y,
        moved: false,
      };
      pointerRef.current = { clientX: event.clientX, clientY: event.clientY };
      overlay.style.cursor = "grabbing";
      capturePointer(overlay, event.pointerId);
      return;
    }

    // 3) 框体：手柄 → resize，其余 → move
    const item = itemsRef.current.find((entry) => entry.id === itemId);
    onSelect(itemId);
    onRulerSelect?.(false);
    overlay.focus({ preventScroll: true });
    // 创建中的临时框先不参与拖动，避免拿到一个马上要被替换的 id 去写库
    if (!item || item.pending) return;

    // 旋转手柄：按在框体上方的圆点上拖动即可旋转
    if (target.closest("[data-rotate-handle]")) {
      const box = boxElement?.getBoundingClientRect();
      if (!box) return;
      gestureRef.current = {
        kind: "rotate",
        itemId,
        centerClientX: box.left + box.width / 2,
        centerClientY: box.top + box.height / 2,
        startAngle: angleFromCenter(
          box.left + box.width / 2,
          box.top + box.height / 2,
          event.clientX,
          event.clientY,
        ),
        startRotation: item.rotation_deg ?? 0,
      };
      pointerRef.current = { clientX: event.clientX, clientY: event.clientY };
      overlay.style.cursor = "grabbing";
      capturePointer(overlay, event.pointerId);
      return;
    }

    const resizing = Boolean(target.closest("[data-resize-handle]"));
    gestureRef.current = {
      kind: resizing ? "resize" : "move",
      itemId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      origin: item,
    };
    pointerRef.current = { clientX: event.clientX, clientY: event.clientY };
    overlay.style.cursor = resizing ? "nwse-resize" : "grabbing";
    capturePointer(overlay, event.pointerId);
    if (boxElement) boxElement.style.zIndex = "30";
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = gestureRef.current;
    if (!gesture) return;
    pointerRef.current = { clientX: event.clientX, clientY: event.clientY };
    if (gesture.kind === "create") {
      const point = normalized(event.clientX, event.clientY);
      gesture.currentX = point.x;
      gesture.currentY = point.y;
    }
    schedulePaint();
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = gestureRef.current;
    const element =
      gesture && (gesture.kind === "move" || gesture.kind === "resize" || gesture.kind === "rotate")
        ? boxRefs.current.get(gesture.itemId)
        : null;
    if (element) element.style.zIndex = "";
    finish(event.clientX, event.clientY);
  };

  /** 滚轮缩放：以鼠标位置为锚点，缩放时指针下的那个点不动。 */
  /** 标尺在屏幕上的几何（像素）：线、端标方向、标签位置都从这里算，避免重复计算 */
  const rulerView = (() => {
    if (!ruler || !pageSize?.width || !pageSize.height) return null;
    const x1 = ruler.x1 * pageSize.width;
    const y1 = ruler.y1 * pageSize.height;
    const x2 = ruler.x2 * pageSize.width;
    const y2 = ruler.y2 * pageSize.height;
    const angle = Math.atan2(y2 - y1, x2 - x1);
    return {
      x1,
      y1,
      x2,
      y2,
      nx: Math.cos(angle + Math.PI / 2),
      ny: Math.sin(angle + Math.PI / 2),
      midX: (x1 + x2) / 2,
      midY: (y1 + y2) / 2,
    };
  })();

  const handleWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    const bounds = viewportRef.current?.getBoundingClientRect();
    if (!bounds) return;
    // 触控板双指/横向滚 = 平移；捏合（ctrlKey）与普通滚轮 = 缩放
    if (event.shiftKey && !event.ctrlKey) {
      onViewChange({ ...view, x: view.x - event.deltaX - event.deltaY, y: view.y });
      return;
    }
    // 一格滚轮（约 100px）≈ 10%；触控板小增量会得到平滑的小步长
    const factor = Math.exp(-event.deltaY / 1000);
    const scale = clamp(view.scale * factor, MIN_SCALE, MAX_SCALE);
    if (scale === view.scale) return;
    const cursorX = event.clientX - bounds.left;
    const cursorY = event.clientY - bounds.top;
    onViewChange({
      scale,
      x: cursorX - ((cursorX - view.x) * scale) / view.scale,
      y: cursorY - ((cursorY - view.y) * scale) / view.scale,
    });
  };

  /**
   * 编号标签排布：柜子挨得近时把标签错开（选中的柜体优先占好位置）。
   * 纯函数算出来的，同样输入同样结果，不会来回抖。
   */
  const labelLayout = useMemo(() => {
    if (!pageSize?.width || !pageSize.height) return {};
    return layoutBoxLabels(
      items.map((item) => {
        const w = item.box_w * pageSize.width;
        const h = item.box_h * pageSize.height;
        const metrics = labelMetricsFor(w, h);
        return {
          id: item.id,
          x: item.box_x * pageSize.width,
          y: item.box_y * pageSize.height,
          w,
          h,
          // 编号大小跟着框体走：量的宽度、内边距都用这个框自己的字号
          numberWidth: measureLabelText(item.label, metrics.fontSize),
          height: metrics.height,
          padding: metrics.padding,
          badgeWidth: item.pending
            ? measureLabelText("创建中", 11) + 12
            : item.size_status === "pending"
              ? measureLabelText("未填尺寸", 11) + 12
              : 0,
        };
      }),
      {
        pageWidth: pageSize.width,
        pageHeight: pageSize.height,
        selectedId,
        padding: 6,
        height: 18,
      },
    );
  }, [items, pageSize?.width, pageSize?.height, selectedId]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
        <span>
          {drawMode ? "画框工具已激活：空白处拖一个框（画完自动回到选择）" : "选择模式：空白处拖拽平移画布"} ·
          滚轮缩放 / R 画框 / Delete 删除 / 方向键微调 / +/- 缩放 / Esc 取消 / 空格或中键平移
        </span>
        <span className="ml-auto">{pageLabel}</span>
      </div>
      {error ? <p className="text-sm text-[var(--color-destructive)]">{error}</p> : null}
      <div
        ref={viewportRef}
        role="application"
        aria-label="户型图画布"
        className="relative min-h-0 flex-1 overflow-hidden rounded-lg border border-[var(--color-border)] bg-neutral-200"
        onWheel={handleWheel}
        onContextMenu={(event) => event.preventDefault()}
      >
        {/* 只缩放内容这一层：外框尺寸不变，边框不会被一起放大 */}
        <div
          ref={stageRef}
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            transformOrigin: "0 0",
          }}
        >
          <div
            ref={overlayRef}
            tabIndex={-1}
            className={`relative mx-auto touch-none select-none bg-white shadow-sm ${
              drawMode ? "cursor-crosshair" : "cursor-default"
            }`}
            // 画布和覆盖层必须是同一个尺寸：只给高度的话，放大后画布仍是"适应宽度"的显示尺寸，
            // 而框是按覆盖层的百分比定位的，两者一错位，缩放后框就会跑偏。
            style={pageSize ? { width: pageSize.width, height: pageSize.height } : undefined}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="户型图"
                className="block select-none"
                style={{ width: pageSize?.width, height: pageSize?.height }}
                draggable={false}
              />
            ) : (
              <canvas
                ref={canvasRef}
                className="block select-none"
                style={{ width: pageSize?.width, height: pageSize?.height }}
              />
            )}
            {items.map((item) => {
              const active = item.id === selectedId;
              const placement = labelLayout[item.id];
              // 编号标签大小跟着框体走（跟 labelLayout 用同一套算法，位置才对得上）
              const metrics = labelMetricsFor(
                item.box_w * (pageSize?.width ?? 0),
                item.box_h * (pageSize?.height ?? 0),
              );
              return (
                <div
                  key={item.id}
                  data-item-id={item.id}
                  ref={(element) => {
                    if (element) boxRefs.current.set(item.id, element);
                    else boxRefs.current.delete(item.id);
                  }}
                  // 颜色跟着柜型走（地柜蓝/吊柜紫/高柜青/岛台橙/展示柜绿/自定义灰）；
                  // 没填尺寸的用虚线边框区分，选中时加一圈光环但保留柜型颜色
                  className={`absolute ${
                    item.pending
                      ? "cursor-progress border-2 border-dashed"
                      : "cursor-grab border-2 transition-colors"
                  } ${active && !item.pending ? "shadow-[0_0_0_2px_rgba(15,23,42,0.25)]" : ""}`}
                  style={{
                    left: `${item.box_x * 100}%`,
                    top: `${item.box_y * 100}%`,
                    width: `${item.box_w * 100}%`,
                    height: `${item.box_h * 100}%`,
                    borderColor: boxColorFor(item.cabinet_type_code).stroke,
                    backgroundColor: boxColorFor(item.cabinet_type_code).fill,
                    borderStyle: !item.pending && item.size_status === "pending" ? "dashed" : "solid",
                    // 旋转：户型图斜着拍的时候把框摆正（拖动时这里是基准，paint 会临时叠加 translate）
                    transform: item.rotation_deg ? `rotate(${item.rotation_deg}deg)` : undefined,
                    transformOrigin: "center",
                  }}
                >
                  <span
                    className="absolute flex items-center gap-1"
                    style={{ left: placement?.dx ?? 0, top: placement?.dy ?? -(metrics.height + 6) }}
                  >
                    {/* 编号：字号/内边距按框体大小算，客户版图纸上也是这个编号对应关系 */}
                    <span
                      className="rounded font-medium text-white"
                      style={{
                        backgroundColor: boxColorFor(item.cabinet_type_code).label,
                        height: metrics.height,
                        lineHeight: `${metrics.height}px`,
                        fontSize: metrics.fontSize,
                        paddingInline: metrics.padding,
                      }}
                    >
                      {item.label}
                    </span>
                    {item.pending ? <Badge tone="muted">创建中</Badge> : null}
                    {!item.pending && item.size_status === "pending" ? (
                      <Badge tone="warn">未填尺寸</Badge>
                    ) : null}
                  </span>
                  {item.pending ? null : (
                    <>
                      {/* 标签被挤走时拉一条细引线回框，避免"这是哪个柜子"看不出来 */}
                      {placement &&
                      (placement.dy <= -(placement.height + 4) * 2 ||
                        placement.dy > item.box_h * (pageSize?.height ?? 0)) ? (
                        <span
                          className="pointer-events-none absolute w-px opacity-50"
                          style={{
                            backgroundColor: boxColorFor(item.cabinet_type_code).stroke,
                            left: placement.dx + 4,
                            top:
                              placement.dy < 0
                                ? placement.dy + placement.height
                                : item.box_h * (pageSize?.height ?? 0),
                            height:
                              placement.dy < 0
                                ? -placement.dy - placement.height
                                : placement.dy - item.box_h * (pageSize?.height ?? 0),
                          }}
                        />
                      ) : null}
                      {active ? (
                        // 旋转手柄：按住圆点拖动即可旋转
                        <span
                          data-rotate-handle
                          title="拖动旋转"
                          className="absolute -top-8 left-1/2 h-3 w-3 -translate-x-1/2 cursor-grab rounded-full border-2 border-white bg-sky-600"
                        />
                      ) : null}
                      {active ? (
                        <span className="pointer-events-none absolute -top-4 left-1/2 h-4 w-px -translate-x-1/2 bg-sky-600/60" />
                      ) : null}
                    </>
                  )}
                  {item.pending ? null : (
                    <span
                      data-resize-handle
                      className="absolute -bottom-1 -right-1 h-3 w-3 cursor-nwse-resize rounded-sm border border-white bg-[var(--color-primary)]"
                    />
                  )}
                </div>
              );
            })}
            <div
              ref={previewRef}
              className="pointer-events-none absolute hidden border-2 border-dashed border-[var(--color-primary)]"
            />
            {/* 标尺：已保存的（蓝色实线 + 长度标签）和正在拖的预览（虚线） */}
            <svg
              role="img"
              aria-label="标尺"
              className="pointer-events-none absolute left-0 top-0"
              width={pageSize?.width ?? 0}
              height={pageSize?.height ?? 0}
            >
              {ruler ? (
                // 标尺对象：粗线 + 两端"垂直竖条"端标，可拖动、可拉伸、可旋转
                <g ref={rulerGroupRef} data-ruler className="pointer-events-auto" style={{ cursor: "grab" }}>
                  {/* 加宽的透明命中区，方便按住整条线拖动 */}
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: SVG 标尺是画布交互对象 */}
                  <line
                    x1={rulerView?.x1}
                    y1={rulerView?.y1}
                    x2={rulerView?.x2}
                    y2={rulerView?.y2}
                    stroke="transparent"
                    strokeWidth={20}
                    onDoubleClick={() => onRulerEdit?.()}
                  />
                  <line
                    x1={rulerView?.x1}
                    y1={rulerView?.y1}
                    x2={rulerView?.x2}
                    y2={rulerView?.y2}
                    stroke={rulerSelected ? RULER_COLOR_ACTIVE : RULER_COLOR}
                    strokeWidth={4}
                    strokeLinecap="round"
                    strokeDasharray="10 6"
                  />
                  {/*
                    端标 = 垂直于标尺的竖条（像建筑图纸的尺寸线），起点/终点一眼可见；
                    每端再叠一条透明粗线做命中区，方便抓。
                  */}
                  {rulerView &&
                    (["start", "end"] as const).map((handle) => {
                      const px = handle === "start" ? rulerView.x1 : rulerView.x2;
                      const py = handle === "start" ? rulerView.y1 : rulerView.y2;
                      return (
                        <g key={handle} data-ruler-handle={handle} className="cursor-crosshair">
                          <line
                            x1={px - rulerView.nx * 14}
                            y1={py - rulerView.ny * 14}
                            x2={px + rulerView.nx * 14}
                            y2={py + rulerView.ny * 14}
                            stroke="transparent"
                            strokeWidth={14}
                          />
                          <line
                            data-ruler-tick={handle}
                            x1={px - rulerView.nx * RULER_TICK_HALF}
                            y1={py - rulerView.ny * RULER_TICK_HALF}
                            x2={px + rulerView.nx * RULER_TICK_HALF}
                            y2={py + rulerView.ny * RULER_TICK_HALF}
                            stroke={rulerSelected ? RULER_COLOR_ACTIVE : RULER_COLOR}
                            strokeWidth={2}
                            strokeLinecap="butt"
                          />
                        </g>
                      );
                    })}
                  <text
                    x={(rulerView?.midX ?? 0) + (rulerView?.nx ?? 0) * 16}
                    y={(rulerView?.midY ?? 0) + (rulerView?.ny ?? 0) * 16}
                    fill={RULER_COLOR}
                    fontSize={14}
                    fontWeight={600}
                    textAnchor="middle"
                  >
                    {Math.round(ruler.lengthMm)} mm
                  </text>
                </g>
              ) : null}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

function setElementRect(element: HTMLElement, rect: BoxRect) {
  element.style.left = `${rect.box_x * 100}%`;
  element.style.top = `${rect.box_y * 100}%`;
  element.style.width = `${rect.box_w * 100}%`;
  element.style.height = `${rect.box_h * 100}%`;
}

/** 覆盖层的像素尺寸（标尺按像素画线） */
function overlayRect(overlay: HTMLDivElement | null): { width: number; height: number } {
  return { width: overlay?.clientWidth ?? 0, height: overlay?.clientHeight ?? 0 };
}

/** 把标尺线段（归一化）按位移平移，并限制在页面内 */
function fitSegment(
  segment: { x1: number; y1: number; x2: number; y2: number },
  dx: number,
  dy: number,
): { x1: number; y1: number; x2: number; y2: number } {
  const minX = Math.min(segment.x1, segment.x2);
  const maxX = Math.max(segment.x1, segment.x2);
  const minY = Math.min(segment.y1, segment.y2);
  const maxY = Math.max(segment.y1, segment.y2);
  const clampedDx = clamp(dx, 0 - minX, 1 - maxX);
  const clampedDy = clamp(dy, 0 - minY, 1 - maxY);
  return {
    x1: segment.x1 + clampedDx,
    y1: segment.y1 + clampedDy,
    x2: segment.x2 + clampedDx,
    y2: segment.y2 + clampedDy,
  };
}

/** 标尺拖动/拉伸时直接改 SVG（不进 React state，跟手且不闪） */
function paintRulerObject(
  group: SVGGElement | null,
  size: { width: number; height: number },
  segment: { x1: number; y1: number; x2: number; y2: number },
) {
  if (!group) return;
  const x1 = segment.x1 * size.width;
  const y1 = segment.y1 * size.height;
  const x2 = segment.x2 * size.width;
  const y2 = segment.y2 * size.height;
  for (const line of group.querySelectorAll("line")) {
    if (line.hasAttribute("data-ruler-tick")) continue; // 端标单独算
    line.setAttribute("x1", String(x1));
    line.setAttribute("y1", String(y1));
    line.setAttribute("x2", String(x2));
    line.setAttribute("y2", String(y2));
  }
  // 端标：垂直于标尺的竖条（跟着线段方向一起转）
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const nx = Math.cos(angle + Math.PI / 2);
  const ny = Math.sin(angle + Math.PI / 2);
  const half = 11;
  const startTick = group.querySelector('[data-ruler-tick="start"]');
  const endTick = group.querySelector('[data-ruler-tick="end"]');
  startTick?.setAttribute("x1", String(x1 - nx * half));
  startTick?.setAttribute("y1", String(y1 - ny * half));
  startTick?.setAttribute("x2", String(x1 + nx * half));
  startTick?.setAttribute("y2", String(y1 + ny * half));
  endTick?.setAttribute("x1", String(x2 - nx * half));
  endTick?.setAttribute("y1", String(y2 - ny * half));
  endTick?.setAttribute("x2", String(x2 + nx * half));
  endTick?.setAttribute("y2", String(y2 + ny * half));
  const text = group.querySelector("text");
  // 标签放在线的侧上方（沿垂直方向抬 16px，避免压住端标）
  text?.setAttribute("x", String((x1 + x2) / 2 + nx * 16));
  text?.setAttribute("y", String((y1 + y2) / 2 + ny * 16));
}

function angleFromCenter(centerX: number, centerY: number, clientX: number, clientY: number): number {
  return (Math.atan2(clientY - centerY, clientX - centerX) * 180) / Math.PI;
}

/** 把角度收敛到 -180..180 */
function normalizeDeg(deg: number): number {
  let value = deg % 360;
  if (value > 180) value -= 360;
  if (value < -180) value += 360;
  return value;
}

/** 指针捕获在合成事件（自动化测试）里会失败，抓不到就当没有——不影响真实指针。 */
function capturePointer(element: HTMLElement, pointerId: number) {
  try {
    element.setPointerCapture(pointerId);
  } catch {
    /* ignore */
  }
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}
